import { AlertCircle, CheckCircle, Download, FileText, Play, Trash2, Upload } from 'lucide-react';
import React, { useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useToast } from '../../../hooks/useToast';
import { buildCsv } from '../../../lib/csv';
import { downloadText } from '../../../lib/download';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Progress } from '../../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Separator } from '../../ui/separator';
import { Textarea } from '../../ui/textarea';

interface BulkUpdateResult {
  awbNumber: string;
  success: boolean;
  error?: string;
  oldStatus?: string;
  newStatus?: string;
}

interface BulkOperationsProps {
  userRole: string;
}

const statusOptions = [
  { value: 'BOOKED', label: 'Booked' },
  { value: 'PICKED_UP', label: 'Picked Up' },
  { value: 'IN_TRANSIT', label: 'In Transit' },
  { value: 'OUT_FOR_DELIVERY', label: 'Out for Delivery' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'RETURNED', label: 'Returned' },
  { value: 'CANCELLED', label: 'Cancelled' }
];

const eventDescriptions = [
  'Package picked up from sender',
  'Package arrived at origin hub',
  'Package in transit',
  'Package arrived at destination hub',
  'Package out for delivery',
  'Package delivered successfully',
  'Delivery attempted - recipient not available',
  'Package returned to sender',
  'Package held at customs',
  'Address correction required',
  'Package cancelled by sender'
];

const parseAwbInput = (input: string, existing: string[]) => {
  const tokens = input
    .split(/[\s,;]+/)
    .map((num) => num.trim().toUpperCase())
    .filter(Boolean);

  const existingSet = new Set(existing.map((num) => num.toUpperCase()));
  const newEntries: string[] = [];
  let duplicates = 0;

  tokens.forEach((token) => {
    if (existingSet.has(token)) {
      duplicates += 1;
      return;
    }
    existingSet.add(token);
    newEntries.push(token);
  });

  return {
    newEntries,
    duplicates,
    total: tokens.length
  };
};

export const BulkOperations: React.FC<BulkOperationsProps> = ({ userRole }) => {
  const [awbNumbers, setAwbNumbers] = useState<string[]>([]);
  const [awbInput, setAwbInput] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<BulkUpdateResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [showFailureDetails, setShowFailureDetails] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { toast } = useToast();
  const { axios } = useAuth();

  const successCount = useMemo(() => results.filter(r => r.success).length, [results]);
  const failureCount = useMemo(() => results.filter(r => !r.success).length, [results]);
  const failedResults = useMemo(() => results.filter(r => !r.success), [results]);
  const hasFailures = failureCount > 0;

  const applyAwbInput = (input: string, sourceLabel: string) => {
    const { newEntries, duplicates, total } = parseAwbInput(input, awbNumbers);
    if (total === 0) return;

    if (newEntries.length > 0) {
      setAwbNumbers([...awbNumbers, ...newEntries]);
    }

    const detailParts = [];
    if (newEntries.length > 0) {
      detailParts.push(`${newEntries.length} added`);
    }
    if (duplicates > 0) {
      detailParts.push(`${duplicates} duplicate${duplicates > 1 ? 's' : ''} skipped`);
    }
    if (detailParts.length === 0) {
      detailParts.push('No new AWBs added');
    }

    toast({
      title: `${sourceLabel} AWB numbers`,
      description: detailParts.join(' • ')
    });
  };

  const handleAddAwbNumbers = () => {
    // Add numbers to the list
    if (!awbInput.trim()) return;

    applyAwbInput(awbInput, 'Added');
    setAwbInput('');
  };

  const handleRemoveAwb = (awbNumber: string) => {
    setAwbNumbers(awbNumbers.filter(num => num !== awbNumber));
  };

  const handleClearAll = () => {
    setAwbNumbers([]);
    setResults([]);
    setShowResults(false);
    setShowFailureDetails(false);
    setProgress(0);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      applyAwbInput(text, 'Imported');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleBulkUpdate = async () => {
    if (awbNumbers.length === 0) {
      toast({ title: "Validation Error", description: "Please add at least one AWB number", variant: "destructive" });
      return;
    }

    if (!newStatus || !eventDescription || !eventLocation) {
      toast({ title: "Validation Error", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }

    setProcessing(true);
    setProgress(0);
    setResults([]);
    setShowResults(true);
    setShowFailureDetails(false);

    try {
      const response = await axios.put('/api/tracking/awb/bulk-status', {
        awbNumbers,
        newStatus,
        eventDescription,
        eventLocation,
        remarks
      });

      const data = response.data;

      if (data.success) {
        setResults(data.data.results);
        setProgress(100);
        toast({ title: "Bulk Update Completed", description: `Processed ${awbNumbers.length} AWB numbers` });
      } else {
        throw new Error(data.error || 'Bulk update failed');
      }
    } catch (error: any) {
      console.error('Error in bulk update:', error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to process bulk update",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleExportResults = () => {
    if (results.length === 0) return;
    const headers = ['AWB Number', 'Status', 'Old Status', 'New Status', 'Error'];
    const rows = results.map(result => [
      result.awbNumber,
      result.success ? 'Success' : 'Failed',
      result.oldStatus || '',
      result.newStatus || '',
      result.error || ''
    ]);

    const csvContent = buildCsv(headers, rows, { quoteStrings: true });
    downloadText(
      csvContent,
      `bulk-update-results-${new Date().toISOString().split('T')[0]}.csv`,
      'text/csv;charset=utf-8;'
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left Column: Input */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Step 1: Add Shipments
            </CardTitle>
            <CardDescription>
              Enter AWB numbers manually or upload a text/CSV file.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={awbInput}
              onChange={(e) => setAwbInput(e.target.value)}
              placeholder="Paste AWB numbers here (separated by new lines or commas)..."
              className="min-h-[150px] font-mono text-sm"
            />
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={handleAddAwbNumbers} disabled={!awbInput.trim()} className="w-full sm:flex-1">
                Add to List
              </Button>
              <div className="relative w-full sm:w-auto">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.csv"
                  onChange={handleFileUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <Button variant="outline" className="w-full sm:w-auto">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload File
                </Button>
              </div>
            </div>
            
            {awbNumbers.length > 0 && (
              <div className="pt-4 border-t">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-600">
                    Selected Shipments ({awbNumbers.length})
                  </span>
                  <Button variant="ghost" size="sm" onClick={handleClearAll} className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Clear
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 max-h-[100px] overflow-y-auto p-2 bg-slate-50 dark:bg-slate-900 rounded-lg border">
                  {awbNumbers.map((awb, index) => (
                    <Badge key={index} variant="secondary" className="bg-white dark:bg-slate-800 border-slate-200">
                      {awb}
                      <button onClick={() => handleRemoveAwb(awb)} className="ml-1 hover:text-red-600">×</button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Right Column: Details & Action */}
      <div className="space-y-6">
        <Card>
           <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-blue-600" />
              Step 2: Update Details
            </CardTitle>
            <CardDescription>
              Select the status and details to apply to all shipments.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">New Status *</label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
               <div className="space-y-2">
                <label className="text-sm font-medium">Location *</label>
                <Input
                  value={eventLocation}
                  onChange={(e) => setEventLocation(e.target.value)}
                  placeholder="e.g. Warehouse"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Event Description *</label>
              <Select value={eventDescription} onValueChange={setEventDescription}>
                <SelectTrigger>
                  <SelectValue placeholder="Select description..." />
                </SelectTrigger>
                <SelectContent>
                  {eventDescriptions.map((desc) => (
                    <SelectItem key={desc} value={desc}>{desc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Remarks</label>
              <Textarea
                placeholder="Optional notes to append..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={2}
              />
            </div>

            <div className="pt-4">
              <Button 
                onClick={handleBulkUpdate}
                disabled={processing || awbNumbers.length === 0 || !newStatus || !eventDescription || !eventLocation}
                className="w-full bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                {processing ? (
                  <>
                    <AlertCircle className="h-4 w-4 mr-2 animate-spin" />
                    Processing Updates...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Update {awbNumbers.length} Shipments
                  </>
                )}
              </Button>
            </div>

            {showResults && (
              <div className="mt-6 animate-in fade-in-50 zoom-in-95 duration-300">
                <Separator className="mb-4" />
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">Operation Results</h4>
                  <div className="flex items-center gap-2">
                    {results.length > 0 && (
                      <Button variant="ghost" size="sm" onClick={handleExportResults}>
                        <Download className="h-3.5 w-3.5 mr-1" />
                        Export CSV
                      </Button>
                    )}
                    {hasFailures && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowFailureDetails((prev) => !prev)}
                      >
                        {showFailureDetails ? 'Hide errors' : `View ${failureCount} error${failureCount > 1 ? 's' : ''}`}
                      </Button>
                    )}
                  </div>
                </div>
                
                {processing ? (
                  <div className="space-y-2">
                    <Progress value={progress} className="h-2" />
                    <p className="text-xs text-center text-muted-foreground">Please wait while we process your request...</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                     <div className="flex gap-2">
                       <div className="flex-1 bg-green-50 text-green-700 p-2 rounded text-center text-sm border border-green-200">
                         <strong>{successCount}</strong> Successful
                       </div>
                       <div className="flex-1 bg-red-50 text-red-700 p-2 rounded text-center text-sm border border-red-200">
                         <strong>{failureCount}</strong> Failed
                       </div>
                     </div>
                     {results.length > 0 && !hasFailures && (
                       <p className="text-xs text-center text-muted-foreground">All shipments updated successfully.</p>
                     )}
                     {showFailureDetails && hasFailures && (
                       <div className="rounded-lg border bg-slate-50 dark:bg-slate-900 p-3 max-h-48 overflow-y-auto">
                         <div className="text-xs font-medium text-slate-500 mb-2">Failed Shipments</div>
                         <div className="space-y-2">
                           {failedResults.map((result, index) => (
                             <div
                               key={`${result.awbNumber}-${index}`}
                               className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 border-b border-slate-200/70 dark:border-slate-800 pb-2 last:border-b-0 last:pb-0"
                             >
                               <span className="font-mono text-xs text-slate-700 dark:text-slate-200">
                                 {result.awbNumber}
                               </span>
                               <span className="text-xs text-red-600 dark:text-red-400">
                                 {result.error || 'Failed to update'}
                               </span>
                             </div>
                           ))}
                         </div>
                       </div>
                     )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
